import { downloadDataAsync, getDataAsync, postDataAsync, putDataAsync } from "../ZeosApiExecutor";
import {
  IScreeningApiReportFormat,
  IScreeningAssignmentRequest,
  IScreeningAssignmentCandidateResponse,
  IScreeningAssignmentResponse,
  IScreeningIntakeUpdateRequest,
  IScreeningReportExportRequest,
  IScreeningReportExportResponse,
  IScreeningReportRequest,
  IScreeningReportResponse,
  IScreeningProjectHandoffRequest,
  IScreeningProjectHandoffResponse,
  IScreeningIncentiveMatchRequestResponse,
  IScreeningOutputIntegrityResponse,
  IScreeningRulesRegulationsResponse,
  IScreeningResearchFindingResponse,
  IScreeningRunHistoryItem,
  IScreeningRunHistoryRequest,
  IScreeningRunExecutionRequest,
  IScreeningRunExecutionResponse,
  IScreeningRunRequest,
  IScreeningRunSummary,
  IScreeningStageResponse,
} from "../contracts/Screening";
import { IIncentiveContract } from "../contracts/Incentive";
import { IScreening } from "./interfaces/IScreening";

const screeningPrefix = "/Screening";

export const Screening: IScreening = {
  createRunAsync: async (request: IScreeningRunRequest) => {
    return await postDataAsync<IScreeningRunSummary>(`${screeningPrefix}/Runs`, request);
  },
  getRunAsync: async (screeningRunId: number) => {
    return await getDataAsync<IScreeningRunSummary>(`${screeningPrefix}/Runs/${screeningRunId}`, "");
  },
  getRunByGuidAsync: async (runGuid: string) => {
    return await getDataAsync<IScreeningRunSummary>(
      `${screeningPrefix}/Runs/Guid/${encodeURIComponent(runGuid)}`,
      ""
    );
  },
  getIncentiveMatchRequestAsync: async (screeningRunId: number) => {
    return await getDataAsync<IScreeningIncentiveMatchRequestResponse>(
      `${screeningPrefix}/Runs/${screeningRunId}/IncentiveMatchRequest`,
      ""
    );
  },
  getIncentiveMatchRequestByGuidAsync: async (runGuid: string) => {
    return await getDataAsync<IScreeningIncentiveMatchRequestResponse>(
      `${screeningPrefix}/Runs/Guid/${encodeURIComponent(runGuid)}/IncentiveMatchRequest`,
      ""
    );
  },
  // QT-9: sandbox-only output integrity checks (Protocol §7).
  getOutputIntegrityAsync: async (screeningRunId: number) => {
    return await getDataAsync<IScreeningOutputIntegrityResponse>(
      `${screeningPrefix}/Runs/${screeningRunId}/OutputIntegrity`,
      ""
    );
  },
  getEligibleIncentivesByRunGuidAsync: async (runGuid: string) => {
    return await getDataAsync<IIncentiveContract[]>(
      `${screeningPrefix}/Runs/Guid/${encodeURIComponent(runGuid)}/EligibleIncentives`,
      ""
    );
  },
  getRunHistoryAsync: async (request?: IScreeningRunHistoryRequest) => {
    const params = new URLSearchParams();
    if (request?.propertyId) params.set("propertyId", request.propertyId.toString());
    if (request?.companyId) params.set("companyId", request.companyId.toString());
    if (request?.serviceProviderId) params.set("serviceProviderId", request.serviceProviderId.toString());
    if (request?.status) params.set("status", request.status);
    if (request?.take) params.set("take", request.take.toString());
    const query = params.toString();
    return await getDataAsync<IScreeningRunHistoryItem[]>(
      `${screeningPrefix}/Runs/History${query ? `?${query}` : ""}`,
      ""
    );
  },
  getLatestPropertyRunAsync: async (propertyId: number) => {
    return await getDataAsync<IScreeningRunSummary>(`${screeningPrefix}/Property/${propertyId}/Latest`, "");
  },
  updateIntakeAsync: async (screeningRunId: number, request: IScreeningIntakeUpdateRequest) => {
    return await putDataAsync<IScreeningRunSummary>(
      `${screeningPrefix}/Runs/${screeningRunId}/Intake`,
      request
    );
  },
  getAssignmentCandidatesAsync: async (screeningRunId: number) => {
    return await getDataAsync<IScreeningAssignmentCandidateResponse[]>(
      `${screeningPrefix}/Runs/${screeningRunId}/AssignmentCandidates`,
      ""
    );
  },
  assignRunAsync: async (screeningRunId: number, request: IScreeningAssignmentRequest) => {
    return await putDataAsync<IScreeningAssignmentResponse>(
      `${screeningPrefix}/Runs/${screeningRunId}/Assignment`,
      request
    );
  },
  startRunAsync: async (screeningRunId: number) => {
    return await postDataAsync<IScreeningRunSummary>(
      `${screeningPrefix}/Runs/${screeningRunId}/Start`,
      {}
    );
  },
  getStageAsync: async (screeningRunId: number, stage?: string) => {
    const query = stage ? `?stage=${encodeURIComponent(stage)}` : "";
    return await getDataAsync<IScreeningStageResponse>(
      `${screeningPrefix}/Runs/${screeningRunId}/Stage${query}`,
      ""
    );
  },
  executeRunAsync: async (
    screeningRunId: number,
    request: IScreeningRunExecutionRequest
  ) => {
    return await postDataAsync<IScreeningRunExecutionResponse>(
      `${screeningPrefix}/Runs/${screeningRunId}/Start`,
      request
    );
  },
  getRulesRegulationsAsync: async (screeningRunId: number) => {
    return await getDataAsync<IScreeningRulesRegulationsResponse>(
      `${screeningPrefix}/Runs/${screeningRunId}/RulesRegulations`,
      ""
    );
  },
  getResearchFindingsAsync: async (screeningRunId: number) => {
    return await getDataAsync<IScreeningResearchFindingResponse[]>(
      `${screeningPrefix}/Runs/${screeningRunId}/Research`,
      ""
    );
  },
  generateReportAsync: async (
    screeningRunId: number,
    request: IScreeningReportRequest
  ) => {
    return await postDataAsync<IScreeningReportResponse>(
      `${screeningPrefix}/Runs/${screeningRunId}/GenerateReports`,
      request
    );
  },
  downloadReportAsync: async (
    screeningRunId: number,
    reportType: string,
    format: IScreeningApiReportFormat
  ) => {
    return await downloadDataAsync(
      `${screeningPrefix}/Runs/${screeningRunId}/Report/${encodeURIComponent(reportType)}?format=${format}`
    );
  },
  exportReportAsync: async (
    screeningRunId: number,
    request: IScreeningReportExportRequest
  ) => {
    return await postDataAsync<IScreeningReportExportResponse>(
      `${screeningPrefix}/Runs/${screeningRunId}/GenerateReports`,
      request
    );
  },
  createProjectHandoffAsync: async (
    screeningRunId: number,
    request: IScreeningProjectHandoffRequest
  ) => {
    return await postDataAsync<IScreeningProjectHandoffResponse>(
      `${screeningPrefix}/Runs/${screeningRunId}/ProjectHandoff`,
      request
    );
  },
};
